import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import { useAppStatePolling } from '@/hooks/useAppStatePolling';
import { cn } from '@/lib/utils';
import apiService from '@/services/apiService';
import Summary from './Overview/components/Summary';

const Overview = (): React.JSX.Element | null => {
	const { t } = useTranslation();
	const posthog = usePostHog();
	const { id, selected } = useParams<{ id: string; selected: string }>();
	const navigate = useNavigate();
	const { applicationManagement } = useAppContext();
	const [isVisible, setIsVisible] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isFetchingData, setIsFetchingData] = useState(!!id);
	const [fetchError, setFetchError] = useState<string | null>(null);

	const fetchApplicationData = applicationManagement.fetch;
	const updateApplication = applicationManagement.update;
	const hasFetchedRef = useRef<string | null>(null);

	useAppStatePolling({ id, enabled: !!id });

	useEffect(() => {
		if (id) posthog?.identify(id);
	}, [id, posthog]);

	useEffect(() => {
		if (id && hasFetchedRef.current !== id) {
			setIsFetchingData(true);
			setFetchError(null);
			fetchApplicationData(id)
				.then(() => {
					hasFetchedRef.current = id;
				})
				.catch((error) => {
					const message = error instanceof Error ? error.message : String(error);
					setFetchError(message);
				})
				.finally(() => {
					setIsFetchingData(false);
				});
		} else {
			setIsFetchingData(false);
		}
	}, [id, fetchApplicationData]);

	useEffect(() => {
		if (!id || !selected) return;
		updateApplication({ selected_sick_leave_short_term_quote_id: selected });
	}, [id, selected, updateApplication]);

	useEffect(() => {
		const timer = setTimeout(() => setIsVisible(true), 100);
		return () => clearTimeout(timer);
	}, []);

	const retryFetch = useCallback(() => {
		if (!id) return;
		setFetchError(null);
		setIsFetchingData(true);
		fetchApplicationData(id)
			.then(() => {
				hasFetchedRef.current = id;
			})
			.catch((error) => {
				const message = error instanceof Error ? error.message : String(error);
				setFetchError(message);
			})
			.finally(() => {
				setIsFetchingData(false);
			});
	}, [id, fetchApplicationData]);

	if (!id || !selected) return null;

	if (isFetchingData) {
		return (
			<section className="container mx-auto px-5 py-4 lg:py-14">
				<div className="w-full lg:w-11/12 xl:w-10/12 2xl:w-9/12 mx-auto mt-8 sm:mt-16 flex justify-center">
					<Spinner size="lg" visible />
				</div>
			</section>
		);
	}

	if (fetchError) {
		return (
			<section className="container mx-auto px-5 py-4 lg:py-14">
				<div className="w-full lg:w-11/12 xl:w-10/12 2xl:w-9/12 mx-auto mt-8 sm:mt-16 text-center">
					<p className="text-destructive mb-4">
						{t('application.fetchError', 'Er is iets misgegaan bij het laden van je gegevens.')}
					</p>
					<Button onClick={retryFetch}>{t('common.retry', 'Opnieuw proberen')}</Button>
				</div>
			</section>
		);
	}

	return (
		<section
			className={cn(
				'container mx-auto px-5 py-4 lg:py-14 transition-opacity duration-300',
				isVisible ? 'opacity-100' : 'opacity-0',
				isSaving && 'opacity-50 pointer-events-none'
			)}
		>
			<div className="w-full lg:w-11/12 xl:w-10/12 2xl:w-10/12 mx-auto mt-6 sm:mt-16">
				<Summary
					id={id}
					isSaving={isSaving}
					onBack={() => {
						navigate(`/aanvulling/${id}/${selected}`);
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}}
					onConfirm={async () => {
						setIsSaving(true);
						const controller = new AbortController();
						const timeout = setTimeout(() => controller.abort(), 4000);
						try {
							await apiService.application.confirmQuoteSelection(id, controller.signal);
						} catch (err) {
							console.error('confirmQuoteSelection failed:', err);
						} finally {
							clearTimeout(timeout);
							setIsSaving(false);
						}
						navigate(`/aanvraag/${id}/${selected}`);
					}}
				/>
			</div>
		</section>
	);
};

export default Overview;
